import React, { Suspense, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SuspenseWrapper = ({ children }) => {
  const location = useLocation();
  const [progress, setProgress] = useState(1);
  const [isComplete, setIsComplete] = useState(false);

  const [time, setTime] = useState(new Date());
  const [networkStatus, setNetworkStatus] = useState({
    type: '5G',
    online: true,
    batteryLevel: 0.8
  });

  useEffect(() => {
    // Update time every minute
    const timeInterval = setInterval(() => {
      setTime(new Date());
    }, 60000);

    // Network status detection
    const updateNetworkInfo = () => {
      setNetworkStatus({
        type: navigator.connection?.effectiveType || '5G',
        online: navigator.onLine,
        batteryLevel: navigator.getBattery?.()?.level || 0.8
      });
    };

    // Battery status
    if (navigator.getBattery) {
      navigator.getBattery().then(battery => {
        battery.addEventListener('levelchange', updateNetworkInfo);
      });
    }

    // Network connection changes
    if (navigator.connection) {
      navigator.connection.addEventListener('change', updateNetworkInfo);
    }

    window.addEventListener('online', updateNetworkInfo);
    window.addEventListener('offline', updateNetworkInfo);

    // Initial update
    updateNetworkInfo();

    return () => {
      clearInterval(timeInterval);
      window.removeEventListener('online', updateNetworkInfo);
      window.removeEventListener('offline', updateNetworkInfo);
    };
  }, []);


  useEffect(() => {
    setProgress(1);
    setIsComplete(false);

    const duration = 15000;
    const intervalTime = 50;
    const totalSteps = duration / intervalTime;
    const increment = 99 / totalSteps;

    let currentProgress = 1;
    const interval = setInterval(() => {
      currentProgress = Math.min(currentProgress + increment, 100);
      setProgress(Math.floor(currentProgress));

      if (currentProgress >= 100) {
        clearInterval(interval);
        // setIsComplete(true);
      }
    }, intervalTime);

    return () => clearInterval(interval);
  }, [location.key]);

  return (
    <div className="fixed inset-0 bg-gray-900 overflow-hidden">
      {/* Digital grid overlay */}
      <div
        className="fixed inset-0 z-0 opacity-20"
        style={{
          backgroundImage: `
            linear-gradient(rgba(34, 211, 238, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34, 211, 238, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Phone-like container */}
      {/* <div
        className={`
          relative z-10 mx-auto w-full h-full max-h-[100dvh]
          sm:max-w-[375px] sm:h-[812px] sm:my-6 sm:rounded-[20px]
          overflow-hidden shadow-2xl bg-gray-950
          flex flex-col border border-gray-800
          sm:border-4 sm:border-gray-900
        `}
        style={{
          aspectRatio: '9 / 19.5', // Standard phone aspect ratio
          maxHeight: 'calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
        }}
      > */}

      <div
        className={`
            relative z-10 mx-auto h-full w-full
            sm:max-w-md sm:h-[95vh] sm:my-4 sm:rounded-lg sm:border sm:border-gray-700
            md:h-[95vh] md:my-4
            overflow-hidden shadow-2xl bg-gray-800 flex flex-col
          `}
      >

        {/* Status Bar */}
        <div className="relative h-10 bg-purple-700 flex items-center justify-between px-4 py-6 border-b border-gray-800/50">
          {/* Current Time */}
          <div>
            <h1 className='text-xs font-bold text-white'>Dekigram</h1>
          </div>
          <div className="text-xs text-white font-medium">
            {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>

          {/* Network/System Indicators */}
          <div className="flex items-center space-x-2">
            {/* Network Type */}
            <span className="text-xs text-white">
              {networkStatus.type.toUpperCase()}
            </span>

            {/* Connection Status */}
            {networkStatus.online && (
              <span className="text-xs text-white">
                {navigator.connection?.type === 'wifi' ? 'Wi-Fi' : 'Cellular'}
              </span>
            )}

          </div>
        </div>

        {/* Content Area */}
        <div
          className="flex-1 overflow-auto bg-gray-800"
          style={{
            padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
          }}
        >
          <Suspense
            fallback={
              <div className="flex h-full w-full p-6 flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-xs font-lora">
                {/* Particle Background */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="particle-field">
                    {[...Array(20)].map((_, i) => (
                      <div
                        key={i}
                        className="particle"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animationDelay: `${Math.random() * 5}s`,
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Main Loader Container */}
                <div className="relative z-10 rounded flex w-full flex-col items-center justify-center text-xs space-y-6 p-6 mx-4 bg-gray-900/80 border border-cyan-500/30 shadow-2xl shadow-cyan-500/20">


                  {/* Progress Info */}
                  <div className="w-full mx-4 space-y-4">

                    {/* <div className="flex justify-between text-xs font-medium text-gray-200 font-lora">
                      <span>Opening a new page, please wait</span>
                    </div> */}

                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-700/50 border border-cyan-500/20">
                      <div
                        className="h-full transition-all duration-300 ease-out glow-progress"
                        style={{
                          width: `${progress}%`,
                          background: 'linear-gradient(90deg, #22d3ee, #8b5cf6)',
                          boxShadow: '0 0 10px rgba(34, 211, 238, 0.5), 0 0 20px rgba(139, 92, 246, 0.3)',
                        }}
                      />
                    </div>

                    <div className="text-left">
                      <p className="text-xs italic text-gray-400 font-lora">
                        {progress < 30
                          ? 'Initializing system components...'
                          : progress < 70
                            ? 'Loading application resources...'
                            : progress < 90
                              ? 'Finalizing setup...'
                              : 'Network connection unstable...'}
                      </p>
                      {isComplete && (
                        <p className="mt-1 text-xs text-cyan-500 animate-pulse font-lora">
                          Almost ready...
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <style jsx>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                  @keyframes particle-move {
                    0% { transform: translateY(0); opacity: 0.3; }
                    50% { opacity: 0.6; }
                    100% { transform: translateY(-100vh); opacity: 0; }
                  }
                  .particle-field {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    pointer-events: none;
                  }
                  .particle {
                    position: absolute;
                    width: 2px;
                    height: 2px;
                    background: rgba(34, 211, 238, 0.4);
                    border-radius: 50%;
                    animation: particle-move 8s linear infinite;
                    box-shadow: 0 0 8px rgba(34, 211, 238, 0.3);
                  }
                  .orbital-spinner {
                    transition: all 0.3s ease;
                  }
                  .glow {
                    animation: glow-pulse 1.5s ease-in-out infinite;
                  }
                  .glow-progress {
                    transition: width 0.3s ease-out;
                  }
                  @keyframes glow-pulse {
                    0%, 100% { box-shadow: 0 0 12px 4px rgba(34, 211, 238, 0.6); }
                    50% { box-shadow: 0 0 16px 6px rgba(34, 211, 238, 0.8); }
                  }
                  .font-lora {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                  }
                `}</style>
              </div>
            }
          >
            {children}
          </Suspense>
        </div>

        {/* Bottom Navigation Bar (Optional) */}
        <div className="h-12 bg-gray-950 flex items-center justify-center border-t border-gray-800/50">
          <div className="w-32 h-1 bg-gray-400 rounded-full"></div>
        </div>
      </div>

      {/* Side Buttons (Optional) */}

    </div>
  );
};

export default SuspenseWrapper;